import { ArrowRight } from "lucide-react";
import { useFormContext, useWatch } from "react-hook-form";
import { JOB_STEPS } from "../CreateJobPage";
import { useCreateJob } from "../hooks/useCreateJobWizard";
import { JobFormValues } from "../schema/jobForm.schema";

const RightPanel = () => {
  const { activeStep, requestStep, isSaving, isEditing, isLoadingDraft, loadError } = useCreateJob();
  const steps = isEditing ? JOB_STEPS.filter(step => step.value >= 2 && step.value <= 6) : JOB_STEPS;
  const progress = steps.findIndex(step => step.value === activeStep) + 1;
  const { control } = useFormContext<JobFormValues>();
  const [title, companyName, creationStrategy] = useWatch({
    control,
    name: ["title", "companyName", "creationStrategy"],
  });

  return (
    <div className="flex flex-col w-[25%] rounded-md border overflow-y-scroll no-scrollbar min-h-0">
      {/* Header */}
      <div className="flex bg-black px-5 py-7">
        <div className="flex flex-col flex-1">
          <span className="uppercase text-light-grey text-xss font-bold tracking-widest mb-3">
            {isEditing ? "Edit job post" : "new job post"}
          </span>
          <span className="font-semibold text-md text-white">
            {title || "Untitled job post"}
          </span>
          <span className="text-xss font-medium text-light-grey">
            {companyName || "Hiring company not selected"}
          </span>
        </div>
        <div className="flex items-center justify-center h-max uppercase text-xsss bg-gray-100 rounded-full font-bold text-light-black px-2 py-1">
          {isEditing ? "Editing" : "draft"}
        </div>
      </div>

      {/* Job Progress section */}
      <div className="flex flex-col">
        <div className="flex items-center justify-between p-4 bg-white">
          <div className="flex flex-col">
            <span className="uppercase font-semibold text-xss text-light-black tracking-wider">
              job post progress
            </span>
            <span className="font-semibold text-xss">
              {JOB_STEPS[activeStep - 1]?.label}
            </span>
          </div>
          <span className="text-xsss font-medium text-light-black">
            <span className="text-sm font-bold text-black mr-1">
              {progress}
            </span>
            of {steps.length}
          </span>
        </div>
        <div className="h-1 w-full bg-slate-200">
          <div
            className="h-full bg-slate-600 transition-all"
            style={{ width: `${(progress / steps.length) * 100}%` }}
          />
        </div>
        {/* Steps */}
        <div className="flex flex-col py-1 bg-white">
          {steps.map((step, stepIndex) => {
            const isActive = step?.value === activeStep;
            return (
              <button
                key={step.value}
                type="button"
                disabled={isSaving || isLoadingDraft || loadError}
                onClick={() => requestStep(step.value)}
                className={`flex items-center justify-between px-4 ${isActive ? "bg-slate-600 py-3" : "py-2 hover:bg-slate-50"}`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`h-6 w-6 rounded-full border flex items-center justify-center font-semibold text-xsss ${isActive ? "text-white" : ""}`}
                  >
                    {String(stepIndex + 1).padStart(2, "0")}
                  </div>
                  <div
                    className={`flex flex-col text-left ${isActive ? "text-white" : "text-slate-600"}`}
                  >
                    <span className="text-xss font-bold">{step?.label}</span>
                    {isActive ? (
                      <span className="text-xsss text-white text-left">
                        {step?.subTagline}
                      </span>
                    ) : null}
                  </div>
                </div>

                <ArrowRight size={14} color="white" />
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col px-5 py-4 bg-white border-t gap-2.5">
        <span className="font-bold text-xss uppercase tracking-widest text-light-black leading-3">
          Requirement source
        </span>
        <div className="flex flex-col">
          <span className="text-xss font-bold text-black">
            {creationStrategy === 'Requirement_Import'
              ? "Imported requirement"
              : "Manual job post"}
          </span>
          <span className="text-xss font-medium text-light-black">
            {companyName || "Hiring company not selected"}
          </span>
        </div>

        {companyName ? null : (
          <div className="rounded-full text-xsss font-bold text-light-black bg-gray-100 w-max py-1 px-2 uppercase">
            Hiring company required
          </div>
        )}
      </div>

      <div className="flex flex-col p-5 bg-gray-100 gap-1.5">
        <span className="font-bold text-xsss uppercase tracking-widest text-light-black">
          {isEditing ? "Existing job" : "Prototype only"}
        </span>
        <span className="text-xsss font-medium text-light-black">
          {isEditing ? "Editing does not charge your wallet or card, republish the job, or change the paid posting duration." : "Draft saving, hiring-company data import, card charging and publishing require authenticated services and audit records."}
        </span>
      </div>
    </div>
  );
};
export default RightPanel;
